import { LayoutDashboard } from 'lucide-react';
import ServiceStoryPage from '../../components/ServiceStoryPage';

const color = 'hsl(243 75% 59%)';

export const seo = {
  title: 'AI Web & App Design',
  tagline: 'Your digital shopfront, built fast',
  metaDesc: 'Websites from £500, apps from £1,500 — built fast with AI workflows. Mobile-first, SEO/AEO/GEO-ready and fully yours to own. For UK small businesses.',
  slug: 'ai-web-app-design',
  whatWeDoItems: [
    { heading: 'A website built around you', body: 'A bespoke design shaped around your brand, your customers, and what you want the site to do — not a template with your logo dropped on top.' },
    { heading: 'Looks great on every phone', body: 'Everything is built mobile-first and tested across devices. Most people will find you on their phone, so that is where your site has to shine.' },
    { heading: 'Ready to be found from day one', body: 'Clean structure, fast loading, and proper setup so Google and the new AI answer engines can find you and show you to customers the moment you go live.' },
    { heading: 'Built fast, without cutting corners', body: 'We use the latest AI-assisted tools to build in weeks rather than months — and pass that time saving on to you, without dropping the quality.' },
    { heading: 'Words that actually sell', body: 'Not sure what to say? We can write clear, friendly copy that speaks to your customers and gently nudges them to get in touch.' },
    { heading: 'You own the lot', body: 'The site, the domain, the content — all yours. No lock-in, no hostage situations. If you ever move on, you take everything with you.' },
  ],
};

export default function AiWebAppDesignPage() {
  return (
    <ServiceStoryPage
      tagline={seo.tagline}
      Icon={LayoutDashboard}
      color={color}
      ctaText="Book a free call"
      heroHeadline="A website that wins you customers, not just compliments"
      heroSub="Your website is usually the first impression a customer gets. We build modern, fast sites that look the part and quietly turn visitors into enquiries — ready in weeks, without the big-agency price tag."
      problem={{
        heading: 'Your website should be working for you — not embarrassing you',
        body: [
          'Maybe you haven’t got a site at all. Maybe you threw one together on a builder years ago and quietly hope nobody looks too closely. Either way, it isn’t bringing you work.',
          'You’re busy running the business, so the website slips down the list. Meanwhile competitors with slicker sites are the ones getting the calls — even when your work is better. It feels unfair, and you’re not sure how to fix it.',
        ],
      }}
      whatYouGetIntro="Everything you need for a professional online presence that actually brings in enquiries — all built for you, all yours to keep."
      whatYouGet={seo.whatWeDoItems}
      howItWorks={[
        { title: 'A friendly chat', body: 'We start with a no-pressure call to understand your business, your customers, and what you want your site to do.' },
        { title: 'We design & build', body: 'We create your site using AI-assisted workflows, keeping you in the loop so it always feels like yours.' },
        { title: 'You review, we refine', body: 'You see it before it goes anywhere. We tweak the wording, images and details until you’re genuinely happy.' },
        { title: 'Launch — and it’s yours', body: 'We put it live, hand over every login, and you own the lot. Need a hand later? We’re still here.' },
      ]}
      proofHeading="See what a proper website can do"
      proofSub="We built Eclipse Paving a site from scratch, then handled their SEO, Google Ads and ongoing growth — turning zero online presence into a steady stream of quality leads."
      pricing={{
        price: 'Websites from £500+ · Apps from £1,500+',
        caption: 'Guide prices — every project is scoped to your needs, with a fixed quote before we start.',
      }}
      finalHeading="Ready for a website you’re proud of?"
      finalSub="Book a free call and we’ll talk through your business, your goals, and exactly how we’d build a site that brings you more of the right customers."
    />
  );
}
